import { ChangeEvent } from "react";

import { CaseAdmin, SpecimenAdmin } from "pwapi";

import usStates from "shared/utils/usStates";

import CustomInput from "components/customInputs/CustomInput";
import DateInput from "components/customInputs/DateInput";
import DateRangeInput from "components/customInputs/DateRangeInput";
import MultiColumnSelectInput from "components/customInputs/MultiColumnSelectInput";
import SelectInput from "components/customInputs/SelectInput";
import TextInput from "components/customInputs/TextInput";

import ClinicFilter from "./ClinicFilter";
import TCCliaCertificateFilter from "./TCCliaCertificateFilter";

type CaseFiltersProps = {
  accession: string;
  grossCompletedAfter: string;
  grossCompletedBefore: string;
  finalizedAtAfter: string;
  finalizedAtBefore: string;
  status: string;
  source: string;
  expectedBy: string;
  biopsyType: string[];
  activeAssignmentName: string;
  orderingDoctorName: string;
  receivedDateAfter: string;
  receivedDateBefore: string;
  expectedNextStatusByRangeAfter: string;
  expectedNextStatusByRangeBefore: string;
  patientName: string;
  patientDateOfBirth: string;
  patientClinicName: string;
  patientClinicLocationState: string;
  patientAddressState: string;
  patientInsuranceInsuranceCompanyName: string;
  grossedByCert: string;
  priority?: string;

  onChange: Function;
  onChangeDateRange: Function;
};

const CaseFilters = (props: CaseFiltersProps) => {
  return (
    <>
      <TextInput
        id="accession"
        value={props.accession}
        label="Accession"
        onChange={(e: any) => props.onChange("accession", e.target.value)}
        onClear={() => props.onChange("accession", "")}
      />
      <DateRangeInput
        id="case_receivedDate"
        label="Date Received"
        values={{
          from: {
            id: "receivedDateAfter",
            name: "received_date_after",
            value: props.receivedDateAfter,
          },
          to: {
            id: "receivedDateBefore",
            name: "received_date_before",
            value: props.receivedDateBefore,
          },
        }}
        onChangeDate={props.onChangeDateRange}
        isClearable
      />
      <TextInput
        id="activeAssignmentName"
        value={props.activeAssignmentName}
        label="Active Assignment"
        onChange={(e: ChangeEvent<HTMLInputElement>) =>
          props.onChange("activeAssignmentName", e.target.value)
        }
        onClear={() => props.onChange("activeAssignmentName", "")}
      />
      <TextInput
        id="orderingDoctorName"
        value={props.orderingDoctorName}
        label="Ordering Provider"
        onChange={(e: ChangeEvent<HTMLInputElement>) => {
          props.onChange("orderingDoctorName", e.target.value);
        }}
        onClear={() => props.onChange("orderingDoctorName", "")}
      />
      <TextInput
        id="patientName"
        value={props.patientName}
        label="Patient"
        onChange={(e: ChangeEvent<HTMLInputElement>) =>
          props.onChange("patientName", e.target.value)
        }
        onClear={() => props.onChange("patientName", "")}
      />
      <CustomInput
        label="Patient Date of Birth"
        id="patientDateOfBirth"
        onClear={() => props.onChange("patientDateOfBirth", "")}
      >
        <DateInput
          value={props.patientDateOfBirth}
          onChange={(e: ChangeEvent<HTMLInputElement>) =>
            props.onChange("patientDateOfBirth", e.target.value)
          }
          showPickerOnFocus
          placeholder=" "
        />
      </CustomInput>

      <SelectInput
        id="patientState"
        label="Patient State"
        defaultLabel="All States"
        options={Object.keys(usStates).map((v) => ({
          label: usStates[v],
          value: v,
        }))}
        value={props.patientAddressState}
        onChange={(e: ChangeEvent<HTMLSelectElement>) =>
          props.onChange("patientAddressState", e.target.value)
        }
      />
      <TextInput
        id="patientInsuranceInsuranceCompanyName"
        value={props.patientInsuranceInsuranceCompanyName}
        label="Patient Insurance Name"
        onChange={(e: ChangeEvent<HTMLInputElement>) =>
          props.onChange("patientInsuranceInsuranceCompanyName", e.target.value)
        }
        onClear={() =>
          props.onChange("patientInsuranceInsuranceCompanyName", "")
        }
      />
      <ClinicFilter
        onChange={(newValue: { value: string }) => {
          props.onChange("patientClinicName", newValue.value);
        }}
        value={props.patientClinicName}
      />
      <SelectInput
        id="patientClinicLocationState"
        label="Clinic State"
        defaultLabel="All States"
        options={Object.keys(usStates).map((v) => ({
          label: usStates[v],
          value: v,
        }))}
        value={props.patientClinicLocationState}
        onChange={(e: ChangeEvent<HTMLSelectElement>) =>
          props.onChange("patientClinicLocationState", e.target.value)
        }
      />
      <DateRangeInput
        id="grossCompleted"
        label="Gross Date"
        values={{
          from: {
            id: "grossCompletedAfter",
            name: "gross_completed_after",
            value: props.grossCompletedAfter,
          },
          to: {
            id: "grossCompletedBefore",
            name: "gross_completed_before",
            value: props.grossCompletedBefore,
          },
        }}
        onChangeDate={props.onChangeDateRange}
        isClearable
      />
      <MultiColumnSelectInput
        label="Biopsy Type"
        id="biopsyType"
        value={props.biopsyType}
        options={Object.values(SpecimenAdmin.biopsy_type)}
        setFilter={(value: ChangeEvent<HTMLInputElement>) =>
          props.onChange("biopsyType", value)
        }
      />
      <SelectInput
        id="status"
        label="Status"
        defaultLabel="All Statuses"
        options={Object.values(CaseAdmin.status).map((v) => ({
          label: v,
          value: v,
        }))}
        value={props.status}
        onChange={(e: ChangeEvent<HTMLSelectElement>) =>
          props.onChange("status", e.target.value)
        }
      />
      <DateRangeInput
        id="expectedNextStatusByRange"
        label="Overdue Date"
        values={{
          from: {
            id: "expectedNextStatusByRangeAfter",
            name: "expected_next_status_by_range_after",
            value: props.expectedNextStatusByRangeAfter,
          },
          to: {
            id: "expectedNextStatusByRangeBefore",
            name: "expected_next_status_by_range_before",
            value: props.expectedNextStatusByRangeBefore,
          },
        }}
        onChangeDate={props.onChangeDateRange}
        isClearable
      />
      <SelectInput
        id="priority"
        label="Case Priority"
        defaultLabel="All Priorities"
        options={Object.values(CaseAdmin.priority).map((v) => ({
          label: v,
          value: v,
        }))}
        value={props.priority || ""}
        onChange={(e: ChangeEvent<HTMLSelectElement>) =>
          props.onChange("priority", e.target.value)
        }
      />
      <SelectInput
        id="source"
        label="Source"
        defaultLabel="All Sources"
        options={Object.values(CaseAdmin.source).map((v) => ({
          label: v,
          value: v,
        }))}
        value={props.source}
        onChange={(e: ChangeEvent<HTMLSelectElement>) =>
          props.onChange("source", e.target.value)
        }
      />
      <TCCliaCertificateFilter
        onChange={({ value }: { value: string }) => {
          props.onChange("grossedByCert", value);
        }}
        value={props.grossedByCert}
      />
    </>
  );
};

export default CaseFilters;
